import { ChronoUnit, Instant } from "@js-joda/core";
import { useClearDatasetData } from "api/api-hooks/datasets-hooks";
import { DatasetInfo } from "api/model/dataset/DatasetInfo";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import DatasetsTitlesDialog from "../datasets-titles-dialog/DatasetsTitlesDialog";

interface IClearDatasetDialogProps {
  selectedDatasets: DatasetInfo[];
  identifiers?: string[];
  closeDialog: () => void;
}

const ClearDatasetDialog = ({ closeDialog, identifiers, selectedDatasets }: IClearDatasetDialogProps) => {
  const [clearAllCheck, setClearAllCheck] = useState(false);
  const [dateFrom, setDateFrom] = useState<Instant>(Instant.now().minus(1, ChronoUnit.HOURS));
  const [dateTo, setDateTo] = useState<Instant>(Instant.now());
  const [isDatasetsDialogVisible, setIsDatasetsDialogVisible] = useState(false);

  const currentIdentifiers: string[] =
    (identifiers as string[]) || selectedDatasets.map(dataset => dataset.identifier as String);

  const clearDataClickHandler = (e: any) => {
    e.preventDefault();
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("action.confirm-action"),
      confirmedCallback: clearData,
    });
  };

  const { mutate } = useClearDatasetData();

  const clearData = () => {
    const timeStart = dateFrom.toString();
    const timeEnd = dateTo.toString();
    const clearAll = clearAllCheck;
    const query = { timeStart, timeEnd, clearAll, identifiers: currentIdentifiers };

    mutate(query, {
      onSuccess: () => {
        easasAlert({
          summary: T("alert.dataset-cleared"),
          detail: T("alert.data-has-been-cleared"),
          severity: "success",
        });
      },
      onError: error => {
        easasAlert({ summary: T("alert.clearing-failed"), detail: `${error}`, severity: "error" });
      },
    });
    closeDialog();
  };

  return (
    <div className="flex flex-column gap-5">
      <div className="flex justify-content-between align-items-center">
        <label htmlFor="clear-all"> {T("label.clear-all-data")}:</label>
        <Checkbox
          id="clear-all"
          className="mr-5"
          checked={clearAllCheck}
          onChange={() => setClearAllCheck(prevState => !prevState)}
        />
      </div>
      {!clearAllCheck && (
        <>
          <div className="flex justify-content-between align-items-center">
            <label htmlFor="date-from"> {T("label.clear-data-from")}:</label>
            <CalendarWrapper id="date-from" value={dateFrom.toDate()} onInstantChange={setDateFrom} />
          </div>
          <div className="flex justify-content-between align-items-center">
            <label htmlFor="date-to"> {T("label.clear-data-to")}:</label>
            <CalendarWrapper
              id="date-to"
              className="mr-0"
              value={dateTo.toDate()}
              onInstantChange={setDateTo}
            />
          </div>
        </>
      )}
      <div className="flex flex-row justify-content-between align-items-center">
        <div>{T("message.datasets-will-be-affected", { count: currentIdentifiers.length })}</div>
        <Button
          className="component-editor-button icon-blue"
          label={T("action.view")}
          icon="pi pi-eye"
          onClick={() => setIsDatasetsDialogVisible(true)}
        />
      </div>
      <div className="flex justify-content-end align-items-center gap-2">
        <Button label="Cancel" icon="pi pi-times" className="component-editor-button" onClick={closeDialog} />
        <Button
          label={T("action.clear-data")}
          icon="pi pi-trash"
          className="component-editor-button icon-red"
          onClick={e => clearDataClickHandler(e)}
        />
      </div>

      {isDatasetsDialogVisible && currentIdentifiers.length > 0 && (
        <DatasetsTitlesDialog
          isVisible={isDatasetsDialogVisible}
          setIsVisible={setIsDatasetsDialogVisible}
          identifiers={currentIdentifiers}
        />
      )}
    </div>
  );
};

export default ClearDatasetDialog;
